import { action, observable, makeObservable, runInAction } from 'mobx'
import FiltersService from 'services/FiltersService'
import { filterOptionsSignature } from 'util/filterOptionsEqual'

const mapBrokerOption = ({ id, displayName }) => ({
    id,
    value: displayName,
})

const mapNameOption = ({ name: id, displayName: value }) => ({
    id,
    value,
})

class FiltersStore {
    brokers = []
    quoteRisks = []
    policyRisks = []
    myInsuranceCompanies = []
    origins = []
    insuranceCompanies = []
    insuranceTypes = []

    loading = {
        brokers: true,
        quoteRisks: true,
        policyRisks: true,
        myInsuranceCompanies: true,
        origins: false,
        insuranceCompanies: false,
        insuranceTypes: false,
    }

    signatures = {
        brokers: '',
        quoteRisks: '',
        policyRisks: '',
        myInsuranceCompanies: '',
        origins: '',
        insuranceCompanies: '',
        insuranceTypes: '',
    }

    inflight = {}

    constructor() {
        makeObservable(this, {
            brokers: observable,
            quoteRisks: observable,
            policyRisks: observable,
            myInsuranceCompanies: observable,
            origins: observable,
            insuranceCompanies: observable,
            insuranceTypes: observable,
            loading: observable,
            loadBrokers: action,
            loadQuoteRisks: action,
            loadPolicyRisks: action,
            loadMyInsuranceCompanies: action,
            loadOrigins: action,
            loadInsuranceCompanies: action,
            loadInsuranceTypes: action,
            loadClientsFilters: action,
            loadQuotesFilters: action,
            loadPoliciesFilters: action,
            clear: action,
        })

        this.filtersService = new FiltersService()
    }

    clear() {
        this.brokers = []
        this.quoteRisks = []
        this.policyRisks = []
        this.myInsuranceCompanies = []
        this.origins = []
        this.insuranceCompanies = []
        this.insuranceTypes = []
        this.signatures = {
            brokers: '',
            quoteRisks: '',
            policyRisks: '',
            myInsuranceCompanies: '',
            origins: '',
            insuranceCompanies: '',
            insuranceTypes: '',
        }
        this.inflight = {}
        this.loading = {
            brokers: true,
            quoteRisks: true,
            policyRisks: true,
            myInsuranceCompanies: true,
            origins: false,
            insuranceCompanies: false,
            insuranceTypes: false,
        }
    }

    hasClientsFilters() {
        return this.brokers.length > 0 || this.origins.length > 0
    }

    hasQuotesFilters() {
        return this.brokers.length > 0 || this.quoteRisks.length > 0 || this.origins.length > 0
    }

    hasPoliciesFilters() {
        return (
            this.brokers.length > 0 ||
            this.policyRisks.length > 0 ||
            this.myInsuranceCompanies.length > 0 ||
            this.origins.length > 0 ||
            this.insuranceCompanies.length > 0 ||
            this.insuranceTypes.length > 0
        )
    }

    async fetchOptions(key, { request, mapItem } = {}) {
        const hasData = this[key].length > 0

        if (this.inflight[key]) {
            return this.inflight[key]
        }

        const promise = (async () => {
            if (!hasData) {
                runInAction(() => {
                    this.loading[key] = true
                })
            }

            try {
                const response = await request()
                const next = (response || []).map(mapItem)
                const nextSignature = filterOptionsSignature(next)

                runInAction(() => {
                    if (nextSignature !== this.signatures[key]) {
                        this[key] = next
                        this.signatures[key] = nextSignature
                    }
                    this.loading[key] = false
                })
            } catch (e) {
                runInAction(() => {
                    this.loading[key] = false
                })
            }
        })()

        this.inflight[key] = promise

        try {
            await promise
        } finally {
            delete this.inflight[key]
        }

        return undefined
    }

    loadBrokers() {
        return this.fetchOptions('brokers', {
            request: () => this.filtersService.getBrokers(),
            mapItem: mapBrokerOption,
        })
    }

    loadQuoteRisks() {
        return this.fetchOptions('quoteRisks', {
            request: () => this.filtersService.getQuoteRisks(),
            mapItem: mapNameOption,
        })
    }

    loadPolicyRisks() {
        return this.fetchOptions('policyRisks', {
            request: () => this.filtersService.getPolicyRisks(),
            mapItem: mapNameOption,
        })
    }

    loadMyInsuranceCompanies() {
        return this.fetchOptions('myInsuranceCompanies', {
            request: () => this.filtersService.getMyInsuranceCompanies(),
            mapItem: mapNameOption,
        })
    }

    loadOrigins() {
        return this.fetchOptions('origins', {
            request: () => this.filtersService.getOrigins(),
            mapItem: mapNameOption,
        })
    }

    loadInsuranceCompanies() {
        return this.fetchOptions('insuranceCompanies', {
            request: () => this.filtersService.getInsuranceCompanies(),
            mapItem: mapNameOption,
        })
    }

    loadInsuranceTypes() {
        return this.fetchOptions('insuranceTypes', {
            request: () => this.filtersService.getInsuranceTypes(),
            mapItem: mapNameOption,
        })
    }

    loadClientsFilters() {
        return Promise.all([this.loadBrokers(), this.loadOrigins()])
    }

    loadQuotesFilters() {
        return Promise.all([this.loadBrokers(), this.loadQuoteRisks(), this.loadOrigins()])
    }

    loadPoliciesFilters() {
        return Promise.all([
            this.loadBrokers(),
            this.loadPolicyRisks(),
            this.loadMyInsuranceCompanies(),
            this.loadOrigins(),
            this.loadInsuranceCompanies(),
            this.loadInsuranceTypes(),
        ])
    }
}

export default FiltersStore
